// Staff assignments (NSR stays) as the supervision screen needs them: which
// campus, which dates, and whether that is still to come. GET /api/nsrs is
// the whole feed for every year, keyed by adultId; staff rows carry adultid.

const iso = (d) => String(d || "").slice(0, 10);

/** Group the stay feed by adult, each list ordered by arrival. */
export function staysByAdult(rows = []) {
  const map = new Map();
  rows.forEach((r) => {
    const key = Number(r?.adultId);
    if (!key) return; // the feed carries orphan rows with adultId 0
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  map.forEach((list) =>
    list.sort((a, b) => iso(a.arrivaldate).localeCompare(iso(b.arrivaldate)))
  );
  return map;
}

/** A stay is past once its departure date has gone by. */
export function isPastStay(stay, today) {
  const end = iso(stay?.departuredate);
  return Boolean(end) && end < iso(today);
}

export function campusOf(stay) {
  return String(stay?.residentialBase || "").trim();
}

/** Campus names as they appear in the feed, deduplicated case-insensitively. */
export function campusOptions(rows = []) {
  const seen = new Map();
  rows.forEach((r) => {
    const name = campusOf(r);
    if (!name) return;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  });
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * The stays to show for one person: current and upcoming by default, all of
 * them with `includePast`, narrowed to one campus when asked.
 */
export function visibleStays(stays = [], { today, includePast = false, campus = "" } = {}) {
  const wanted = String(campus || "").toLowerCase();
  return stays.filter((s) => {
    if (!includePast && isPastStay(s, today)) return false;
    if (wanted && campusOf(s).toLowerCase() !== wanted) return false;
    return true;
  });
}

/** "Dollar, 28 Jun – 12 Jul" — the year only when it isn't the current one. */
export function stayLabel(stay, today) {
  const start = iso(stay?.arrivaldate);
  const end = iso(stay?.departuredate);
  const thisYear = iso(today).slice(0, 4);
  const short = (value) => {
    if (!value) return "?";
    const [y, m, d] = value.split("-");
    const month = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ][Number(m) - 1];
    return `${Number(d)} ${month}${y === thisYear ? "" : ` ${y}`}`;
  };
  const campus = campusOf(stay) || "No campus";
  return `${campus}, ${short(start)} – ${short(end)}`;
}

/**
 * Who to offer, given the filters. Anyone already supervised stays on the
 * list whatever the filters say — otherwise ticking a campus would hide
 * people from the list being edited, and they would look removed.
 */
export function filterCandidates(
  staff = [],
  { staysByAdultId, today, includePast = false, campus = "", text = "", selected = new Set() } = {}
) {
  const needle = String(text || "").trim().toLowerCase();
  const wanted = String(campus || "").trim();

  return staff
    .filter((s) => {
      if (selected.has(Number(s.staffid))) return true;

      if (needle && !`${s.name || ""} ${s.username || ""}`.toLowerCase().includes(needle)) {
        return false;
      }

      // No campus chosen: everyone matching the text, whether or not they are
      // assigned anywhere — supervision is not tied to a stay.
      if (!wanted) return true;

      const stays = staysByAdultId?.get(Number(s.adultid)) || [];
      return visibleStays(stays, { today, includePast, campus: wanted }).length > 0;
    })
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
}
